import { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  MapPin,
  Download,
  Info,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const BulkUploadBoxes = () => {
  const fileInputRef = useRef(null);
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  
  // Apply state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setPreviewData(null);
      setApplyResult(null);
    }
  };

  const handleUploadPreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file first');
      return;
    }
    
    setUploading(true);
    setPreviewData(null);
    setApplyResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API}/admin/honesty-boxes/bulk/preview`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setPreviewData(response.data);
      
      if (response.data.valid_rows === 0) {
        toast.error('No valid rows found. Please check the errors and fix your CSV.');
      } else if (response.data.invalid_rows > 0) {
        toast.warning(`${response.data.valid_rows} valid, ${response.data.invalid_rows} with errors`);
      } else {
        toast.success(`All ${response.data.valid_rows} rows are valid!`);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleApply = async () => {
    if (confirmationPhrase !== 'CREATE') {
      toast.error('Please type CREATE to confirm');
      return;
    }
    
    setApplyLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/honesty-boxes/bulk/apply`,
        {
          preview_token: previewData.preview_token,
          confirmation_phrase: confirmationPhrase,
          rows: previewData.rows
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApplyResult(response.data);
      setConfirmDialogOpen(false);
      setConfirmationPhrase('');
      setPreviewData(null);
      setSelectedFile(null);
      
      toast.success(`Successfully created ${response.data.created_count} honesty box(es)!`);
    } catch (error) {
      console.error('Apply error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create boxes');
    } finally {
      setApplyLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `name,category,description,address,lat,lng,hours,contact_info,is_claimed
"Smith Family Farm Stand",vegetables,"Fresh organic vegetables from our garden","123 Main St, Noosa QLD 4567",-26.3947,153.0891,"7am - 6pm daily","0412 345 678",false
"Honey Haven",jams,"Local raw honey and homemade preserves","456 Beach Rd, Coolum QLD 4573",-26.5281,153.0856,"8am - 5pm",email@example.com,false
"Sunny Eggs",eggs,"Free range eggs from happy hens","789 Farm Lane, Eumundi QLD 4562",-26.4755,152.9515,"Dawn to Dusk",,true`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'honesty_boxes_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template downloaded!');
  };

  const openConfirmDialog = () => {
    setConfirmationPhrase('');
    setConfirmDialogOpen(true);
  };

  const validRows = previewData?.rows?.filter(r => r.is_valid) || [];
  const invalidRows = previewData?.rows?.filter(r => !r.is_valid) || [];

  return (
    <div className="space-y-6" data-testid="bulk-upload-boxes">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Upload className="w-6 h-6 text-green-600" />
          Bulk Upload Honesty Boxes
        </h2>
        <p className="text-gray-600 mt-1">
          Upload a CSV file to create multiple honesty boxes at once
        </p>
      </div>

      {/* CSV Format Info */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600" />
            CSV File Requirements
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2">Required Columns:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">name</code> - Stall name (max 200 chars)</li>
                <li><code className="bg-gray-100 px-1 rounded">category</code> - fruits, vegetables, eggs, jams, flowers, baked_goods, homegrown, homemade, upcycled, secondhand, other</li>
                <li><code className="bg-gray-100 px-1 rounded">description</code> - Description (max 2000 chars)</li>
                <li><code className="bg-gray-100 px-1 rounded">address</code> - Full address</li>
                <li><code className="bg-gray-100 px-1 rounded">lat</code> - Latitude (-90 to 90)</li>
                <li><code className="bg-gray-100 px-1 rounded">lng</code> - Longitude (-180 to 180)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2">Optional Columns:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">hours</code> - Operating hours</li>
                <li><code className="bg-gray-100 px-1 rounded">contact_info</code> - Phone or email</li>
                <li><code className="bg-gray-100 px-1 rounded">is_claimed</code> - true/false (default: false)</li>
              </ul>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="border-blue-400 text-blue-700"
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV Template
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Step 1: Upload CSV File</CardTitle>
          <CardDescription>
            Select your CSV file containing honesty box data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="csv-file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="border-dashed border-2 h-20 px-8"
              data-testid="select-file-btn"
            >
              <FileSpreadsheet className="w-6 h-6 mr-3 text-green-600" />
              {selectedFile ? selectedFile.name : 'Select CSV File'}
            </Button>
            
            {selectedFile && (
              <Button
                onClick={handleUploadPreview}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 h-20"
                data-testid="upload-preview-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload & Preview
                  </>
                )}
              </Button>
            )}
          </div>
          
          {selectedFile && (
            <p className="text-sm text-gray-500">
              Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </CardContent>
      </Card>

      {/* Preview Results */}
      {previewData && (
        <Card className="border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Step 2: Review Preview
              </span>
              <div className="flex gap-2">
                <Badge className="bg-green-100 text-green-800">
                  {previewData.valid_rows} valid
                </Badge>
                {previewData.invalid_rows > 0 && (
                  <Badge className="bg-red-100 text-red-800">
                    {previewData.invalid_rows} with errors
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Invalid Rows (Errors) */}
            {invalidRows.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Rows with Errors ({invalidRows.length})
                </h4>
                <div className="bg-red-50 rounded-lg p-3 max-h-60 overflow-y-auto">
                  {invalidRows.map((row) => (
                    <div key={row.row_number} className="mb-2 pb-2 border-b border-red-100 last:border-0">
                      <p className="font-medium text-red-800">
                        Row {row.row_number}: {row.name || '(empty name)'}
                      </p>
                      <ul className="text-sm text-red-600 list-disc list-inside">
                        {row.errors.map((error, i) => (
                          <li key={i}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Valid Rows Table */}
            {validRows.length > 0 && (
              <>
                <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Valid Rows ({validRows.length})
                </h4>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-medium">#</th>
                        <th className="text-left p-3 font-medium">Name</th>
                        <th className="text-left p-3 font-medium">Category</th>
                        <th className="text-left p-3 font-medium">Address</th>
                        <th className="text-left p-3 font-medium">Coords</th>
                        <th className="text-left p-3 font-medium">Claimed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.slice(0, 20).map((row) => (
                        <tr key={row.row_number} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-gray-500">{row.row_number}</td>
                          <td className="p-3 font-medium">{row.name}</td>
                          <td className="p-3">
                            <Badge variant="outline">{row.category}</Badge>
                          </td>
                          <td className="p-3 text-gray-600 max-w-[200px] truncate">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              {row.address}
                            </span>
                          </td>
                          <td className="p-3 text-xs text-gray-500 font-mono">
                            {row.lat?.toFixed(4)}, {row.lng?.toFixed(4)}
                          </td>
                          <td className="p-3">
                            <Badge className={row.is_claimed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {row.is_claimed ? 'Yes' : 'No'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {validRows.length > 20 && (
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      ... and {validRows.length - 20} more valid rows
                    </p>
                  )}
                </div>

                {/* Apply Button */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    <strong>{validRows.length}</strong> honesty box(es) will be created
                  </p>
                  <Button 
                    onClick={openConfirmDialog}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="apply-bulk-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Create {validRows.length} Box(es)
                  </Button>
                </div>
              </>
            )}

            {validRows.length === 0 && (
              <div className="text-center py-8">
                <XCircle className="w-12 h-12 text-red-300 mx-auto mb-3" />
                <p className="text-gray-500">No valid rows to create</p>
                <p className="text-sm text-gray-400">Please fix the errors above and re-upload</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Apply Result */}
      {applyResult && (
        <Card className={applyResult.failed_count > 0 ? 'border-amber-200' : 'border-green-200'}>
          <CardHeader className={applyResult.failed_count > 0 ? 'bg-amber-50' : 'bg-green-50'}>
            <CardTitle className="text-lg flex items-center gap-2">
              {applyResult.failed_count > 0 ? (
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              Bulk Upload Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-700" data-testid="bulk-created-count">
                  {applyResult.created_count}
                </p>
                <p className="text-sm text-green-600">Created</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-700" data-testid="bulk-skipped-count">
                  {applyResult.skipped_count}
                </p>
                <p className="text-sm text-gray-600">Skipped (duplicates)</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-700" data-testid="bulk-failed-count">
                  {applyResult.failed_count}
                </p>
                <p className="text-sm text-red-600">Failed</p>
              </div>
            </div>

            {applyResult.failures && applyResult.failures.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 rounded-lg">
                <h4 className="font-medium text-red-800 mb-2">Failed Items:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {applyResult.failures.map((failure, index) => (
                    <li key={index}>
                      <XCircle className="w-3 h-3 inline mr-1" />
                      Row {failure.row_number} ({failure.name}): {failure.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {applyResult.created_boxes && applyResult.created_boxes.length > 0 && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Created Boxes:</h4>
                <div className="flex flex-wrap gap-2">
                  {applyResult.created_boxes.slice(0, 10).map((box) => (
                    <Badge key={box.id} className="bg-green-100 text-green-700">
                      {box.name}
                    </Badge>
                  ))}
                  {applyResult.created_boxes.length > 10 && (
                    <Badge variant="outline">+{applyResult.created_boxes.length - 10} more</Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Bulk Creation
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                You are about to create <strong>{validRows.length}</strong> new honesty box(es) from your CSV file.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>This action will:</strong>
                </p>
                <ul className="text-sm text-amber-700 list-disc list-inside mt-1">
                  <li>Create new honesty boxes on the map</li>
                  <li>Auto-approve all boxes (admin upload)</li>
                  <li>Boxes marked as "claimed" will be assigned to you</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPhrase">
                  Type <strong>CREATE</strong> to confirm:
                </Label>
                <Input
                  id="confirmPhrase"
                  value={confirmationPhrase}
                  onChange={(e) => setConfirmationPhrase(e.target.value.toUpperCase())}
                  placeholder="Type CREATE"
                  className="font-mono"
                  data-testid="bulk-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applyLoading} data-testid="bulk-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApply}
              disabled={applyLoading || confirmationPhrase !== 'CREATE'}
              className="bg-green-600 hover:bg-green-700"
              data-testid="bulk-confirm-apply"
            >
              {applyLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create {validRows.length} Box(es)
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BulkUploadBoxes;
